import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { PlayArrow, Pause, RestartAlt } from "@mui/icons-material";
import image_0901 from "../image/image_0901.jpg";

// Your data object
// ----- 20 HIGH‑QUALITY EVENT IMAGES (Unsplash) -----
const eventImages = [
  {
    id: 1,
    url: "https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&auto=format&fit=crop",
    title: "Gala Night",
  },
  {
    id: 2,
    url: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=800&auto=format&fit=crop",
    title: "Wedding Celebration",
  },
  {
    id: 3,
    url: "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=800&auto=format&fit=crop",
    title: "Corporate Summit",
  },
  {
    id: 4,
    url: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&auto=format&fit=crop",
    title: "Music Festival",
  },
  {
    id: 5,
    url: "https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=800&auto=format&fit=crop",
    title: "Fashion Show",
  },
  {
    id: 6,
    url: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800&auto=format&fit=crop",
    title: "Art Exhibition",
  },
  {
    id: 7,
    url: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=800&auto=format&fit=crop",
    title: "Outdoor Concert",
  },
  {
    id: 8,
    url: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=800&auto=format&fit=crop",
    title: "Wedding Reception",
  },
  {
    id: 9,
    url: "https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?w=800&auto=format&fit=crop",
    title: "Corporate Dinner",
  },
  {
    id: 10,
    url: "https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=800&auto=format&fit=crop",
    title: "Team Building",
  },
  {
    id: 11,
    url: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=800&auto=format&fit=crop",
    title: "Concert Crowd",
  },
  {
    id: 12,
    url: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=800&auto=format&fit=crop",
    title: "New Year Party",
  },
  {
    id: 13,
    url: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800&auto=format&fit=crop",
    title: "Fundraising Gala",
  },
  {
    id: 14,
    url: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=800&auto=format&fit=crop",
    title: "Photography Workshop",
  },
  {
    id: 15,
    url: image_0901,
    title: "Birthday Party",
  },
  {
    id: 16,
    url: "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=800&auto=format&fit=crop",
    title: "Wedding Decor",
  },
  {
    id: 17,
    url: "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=800&auto=format&fit=crop",
    title: "Festival",
  },
  {
    id: 18,
    url: "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=800&auto=format&fit=crop",
    title: "Business Conference",
  },
  {
    id: 19,
    url: "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=800&auto=format&fit=crop",
    title: "Award Ceremony",
  },
  {
    id: 20,
    url: "https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=800&auto=format&fit=crop",
    title: "After Party",
  },
];

// ----- Styled Components – NO SCROLL, RESPONSIVE, ONE LINE -----
const CardsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: theme.spacing(5),
  position: "relative",
  flexWrap: "nowrap", // 🔥 One line only – no wrap
  width: "100%", // Full width of parent
  // 🔥 NO overflowX – scroll removed completely
  [theme.breakpoints.down("md")]: {
    gap: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(2),
  },
}));

const StyledCard = styled(Paper)(({ theme }) => ({
  flex: "1 1 0", // 🔥 Allow equal distribution and shrinking
  minWidth: 0, // 🔥 Allow shrinking below content size
  maxWidth: 600, // 🔥 Cap at 600px on large screens
  width: "100%", // Use available space
  height: 500, // Fixed height (adjustable)
  borderRadius: theme.shape.borderRadius * 2,
  overflow: "hidden",
  boxShadow: theme.shadows[12],
  position: "relative",
  zIndex: 1,
  transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: theme.shadows[20],
  },
  [theme.breakpoints.down("md")]: {
    height: 380,
    maxWidth: 450,
  },
  [theme.breakpoints.down("sm")]: {
    height: 280,
    maxWidth: "45vw", // 🔥 Two cards fit side‑by‑side without scroll
  },
}));

const CardImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

const Placeholder = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.text.secondary,
  fontSize: "1.5rem",
  fontWeight: 500,
  flexDirection: "column",
  gap: theme.spacing(2),
}));

// Flying image now uses dynamic width/height from the source card
const FlyingImage = styled("img")(
  ({ theme, $isFlying, $startPos, $endPos }) => ({
    position: "fixed",
    width: $startPos?.width || "auto", // 🔥 Exact width of source card
    height: $startPos?.height || "auto", // 🔥 Exact height of source card
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[16],
    objectFit: "cover",
    zIndex: 9999,
    pointerEvents: "none",
    animation: $isFlying ? "fly 0.8s ease-in-out forwards" : "none",
    left: $startPos?.left || 0,
    top: $startPos?.top || 0,

    "@keyframes fly": {
      "0%": {
        left: $startPos?.left,
        top: $startPos?.top,
        transform: "scale(1) rotate(0deg)",
        opacity: 1,
      },
      "50%": {
        transform: "scale(1.08) rotate(3deg)",
        opacity: 0.9,
      },
      "100%": {
        left: $endPos?.left,
        top: $endPos?.top,
        transform: "scale(0.95) rotate(0deg)",
        opacity: 0.8,
      },
    },
  }),
);

// ----- MAIN COMPONENT -----
const TwoCardAutoFly = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State for the 20‑image fly loop
  const [leftImage, setLeftImage] = useState(eventImages[0]);
  const [rightImage, setRightImage] = useState(null);
  const [nextImageIndex, setNextImageIndex] = useState(1);
  const [isFlying, setIsFlying] = useState(false);
  const [startPos, setStartPos] = useState(null);
  const [endPos, setEndPos] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const timeoutRef = useRef(null);

  const sourceCardId = leftImage ? "card-left" : "card-right";
  const destCardId = leftImage ? "card-right" : "card-left";

  useEffect(() => {
    if (!isPlaying) return;

    const flyToOtherCard = () => {
      const fromCard = document.getElementById(sourceCardId);
      const toCard = document.getElementById(destCardId);

      if (!fromCard || !toCard) return;

      const fromRect = fromCard.getBoundingClientRect();
      const toRect = toCard.getBoundingClientRect();

      // Save exact position AND dimensions of the source card
      setStartPos({
        left: fromRect.left + "px",
        top: fromRect.top + "px",
        width: fromRect.width + "px", // 🔥 Capture current width
        height: fromRect.height + "px", // 🔥 Capture current height
      });

      setEndPos({
        left: toRect.left + "px",
        top: toRect.top + "px",
      });

      setIsFlying(true);

      timeoutRef.current = setTimeout(() => {
        setIsFlying(false);

        if (leftImage) {
          setRightImage(eventImages[nextImageIndex]);
          setLeftImage(null);
        } else {
          setLeftImage(eventImages[nextImageIndex]);
          setRightImage(null);
        }

        setNextImageIndex((prev) => (prev + 1) % eventImages.length);
        setStartPos(null);
        setEndPos(null);
      }, 800);
    };

    const initialTimeout = setTimeout(flyToOtherCard, 500);
    const interval = setInterval(flyToOtherCard, 2000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [
    leftImage,
    rightImage,
    nextImageIndex,
    sourceCardId,
    destCardId,
    isPlaying,
  ]);

  const togglePlay = () => setIsPlaying(!isPlaying);

  const reset = () => {
    setIsPlaying(false);
    setIsFlying(false);
    setLeftImage(eventImages[0]);
    setRightImage(null);
    setNextImageIndex(1);
    setStartPos(null);
    setEndPos(null);
  };

  return (
    <Box sx={{ py: 10, backgroundColor: theme.palette.background.default }}>
      <Container maxWidth="xl">
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 6,
          }}
        >
          🕊️ Our Organise Events
        </Typography>

        <CardsContainer>
          {/* LEFT CARD */}
          <StyledCard id="card-left">
            {leftImage ? (
              <CardImage src={leftImage.url} alt={leftImage.title} />
            ) : (
              <Placeholder>
                <span style={{ fontSize: "3rem" }}>✨</span>
                Waiting for next image
              </Placeholder>
            )}
            {leftImage && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                  color: "white",
                  p: 3,
                }}
              >
                <Typography variant="h5">{leftImage?.title}</Typography>
              </Box>
            )}
          </StyledCard>

          {/* RIGHT CARD */}
          <StyledCard id="card-right">
            {rightImage ? (
              <CardImage src={rightImage.url} alt={rightImage.title} />
            ) : (
              <Placeholder>
                <span style={{ fontSize: "3rem" }}>✨</span>
                Waiting for next image
              </Placeholder>
            )}
            {rightImage && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                  color: "white",
                  p: 3,
                }}
              >
                <Typography variant="h5">{rightImage?.title}</Typography>
              </Box>
            )}
          </StyledCard>
        </CardsContainer>

        {/* FLYING IMAGE – now matches source card dimensions exactly */}
        {isFlying && startPos && endPos && (
          <FlyingImage
            src={eventImages[nextImageIndex].url}
            $isFlying={isFlying}
            $startPos={startPos}
            $endPos={endPos}
            alt="flying event"
          />
        )}

        {/* CONTROLS */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mt: 6 }}>
          <IconButton
            onClick={togglePlay}
            sx={{
              bgcolor: "primary.main",
              color: "white",
              "&:hover": { bgcolor: "primary.dark" },
              width: 60,
              height: 60,
            }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
          <IconButton
            onClick={reset}
            sx={{
              bgcolor: "secondary.main",
              color: "white",
              "&:hover": { bgcolor: "secondary.dark" },
              width: 60,
              height: 60,
            }}
          >
            <RestartAlt />
          </IconButton>
        </Box>

        {/* STATUS */}
        <Typography
          variant="body1"
          align="center"
          sx={{ mt: 4 }}
          color="text.secondary"
        >
          {isPlaying ? "🔵 Auto‑flying" : "⏸️ Paused"} –{" "}
          {leftImage ? "Image on LEFT" : "Image on RIGHT"} • Next:{" "}
          {eventImages[nextImageIndex].title}
        </Typography>
      </Container>
    </Box>
  );
};

export default TwoCardAutoFly;
